@if (answers !== undefined) {
  <div
    class="px-8 py-6 bg-neutral overflow-y-auto"
    style="max-height: calc(100vh - 80px)"
  >
    <!-- Header row -->
    <div class="flex items-start justify-between mb-5">
      <div class="flex flex-col gap-1">
        <h2
          class="font-spaceGrotesk font-semibold text-[22px] text-inverted-neutral"
        >
          Response #{{ data.index }}
        </h2>
        <p class="inter-300 text-base-200 text-[13px]">
          {{
            data.surveyRespondentGuid ? 'Respondent: ' : 'Anonymous response'
          }}
          @if (data.surveyRespondentGuid) {
            <span
              class="text-kek-purple cursor-pointer underline inter-500"
              (click)="navigateToSurveyRespondentDetailsView()"
            >
              {{ data.surveyRespondentName }}
            </span>
          }
        </p>
        <div
          class="mt-2 rounded-[8px] border border-textbox-border bg-base-100 px-3 py-2"
        >
          <div
            class="inter-500 text-[11px] uppercase tracking-wide text-base-200"
          >
            Questionnaire Instance GUID
          </div>
          <div class="mt-1 flex items-center gap-2">
            <span
              class="inter-300 text-[12px] text-inverted-neutral break-all"
              >{{ data.questionnaireInstanceGuid }}</span
            >
            <button
              type="button"
              (click)="copyQuestionnaireInstanceGuid($event)"
              class="tooltip tooltip-bg-200 flex h-[28px] w-[28px] flex-shrink-0 items-center justify-center rounded-full border border-textbox-border text-base-200 hover:bg-textbox-fill hover:text-inverted-neutral"
              data-tip="Copy questionnaire instance GUID"
            >
              <span class="material-symbols-outlined text-[17px]"
                >content_copy</span
              >
            </button>
          </div>
        </div>
        <div
          class="mt-2 rounded-[8px] border border-textbox-border bg-base-100 px-3 py-2"
        >
          <div
            class="inter-500 text-[11px] uppercase tracking-wide text-base-200"
          >
            Parent Response
          </div>
          @if (hasParentQuestionnaireResponse()) {
            <button
              type="button"
              (click)="navigateToParentQuestionnaireResponse()"
              class="mt-1 inline-flex w-fit items-center gap-2 rounded-full border border-kek-purple/40 bg-kek-purple/10 px-3 py-1.5 inter-500 text-[12px] text-kek-purple hover:bg-kek-purple/20"
            >
              <span class="material-symbols-outlined text-[16px]"
                >account_tree</span
              >
              <span>
                Open parent response
                @if (data.parentQuestionnaireName) {
                  <span>({{ data.parentQuestionnaireName }})</span>
                }
              </span>
            </button>
          } @else {
            <div class="mt-1 inter-300 text-[12px] text-base-200">
              No parent response metadata available for this instance.
            </div>
          }
        </div>
        @if (hasInvitationMetadata()) {
          <div
            class="mt-2 rounded-[8px] border border-textbox-border bg-base-100 px-3 py-2"
          >
            <div
              class="inter-500 text-[11px] uppercase tracking-wide text-base-200"
            >
              Invitation
            </div>
            <div
              class="mt-1 flex flex-col gap-1 inter-300 text-[12px] text-inverted-neutral"
            >
              <div>
                <span class="text-base-200">Invited by</span>
                <span class="ml-2">{{ getInvitedByLabel() }}</span>
              </div>
              @if (data.invitedAt) {
                <div>
                  <span class="text-base-200">Invited at</span>
                  <span class="ml-2">{{
                    data.invitedAt | date: 'dd/MM/yyyy HH:mm'
                  }}</span>
                </div>
              }
            </div>
          </div>
        }
      </div>

      <!-- Status badge -->
      <div class="flex flex-col items-end gap-2">
        <span
          [ngClass]="
            data.status === QuestionnaireInstanceStatus.Finished
              ? 'border-status-green-border text-status-green bg-status-green-soft'
              : 'border-companion-amber-border text-companion-amber bg-companion-amber-soft'
          "
          class="inter-500 text-[12px] px-3 py-1 rounded-full border"
        >
          {{
            data.status === QuestionnaireInstanceStatus.Finished
              ? 'Complete'
              : 'Incomplete'
          }}
        </span>
        @if (data.isTest) {
          <span
            class="inter-500 text-[12px] px-3 py-1 rounded-full border border-red-400 text-red-600 bg-red-50"
          >
            Test Instance
          </span>
        }
      </div>
    </div>

    <!-- Meta info + Actions side by side -->
    <div class="grid grid-cols-2 gap-4 mb-6">
      <!-- Meta info card -->
      <div class="bg-base-100 rounded-[14px] px-5 py-4 flex flex-col gap-2">
        <p
          class="inter-500 text-[13px] text-base-200 uppercase tracking-wider mb-1"
        >
          Details
        </p>
        <div class="flex justify-between inter-300 text-[13px]">
          <span class="text-base-200">Date started</span>
          <span class="text-inverted-neutral">{{
            data.startedAt
              ? (data.startedAt | date: 'dd/MM/yyyy')
              : '--/--/----'
          }}</span>
        </div>
        <div class="flex justify-between inter-300 text-[13px]">
          <span class="text-base-200">Time started</span>
          <span class="text-inverted-neutral">{{
            data.startedAt ? (data.startedAt | date: 'HH:mm') : '--:--'
          }}</span>
        </div>
        <div class="flex justify-between inter-300 text-[13px]">
          <span class="text-base-200">Time spent</span>
          <span class="text-inverted-neutral">{{ data.timeTaken || '—' }}</span>
        </div>
      </div>

      <!-- Actions card -->
      <div class="bg-base-100 rounded-[14px] px-5 py-4">
        <p
          class="inter-500 text-[13px] text-base-200 uppercase tracking-wider mb-3"
        >
          Actions
        </p>
        <div class="flex items-center justify-between">
          <span class="inter-300 text-[14px] text-inverted-neutral"
            >Mark as Test</span
          >
          <label class="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              class="sr-only peer"
              [(ngModel)]="data.isTest"
              (ngModelChange)="updateQuestionnaireInstance()"
            />
            <span
              class="border-[2px] bg-base-100 w-11 after:bg-[#737373] peer-checked:bg-white peer-checked:after:bg-[#00c55a] h-6 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:border after:rounded-full after:h-5 after:w-5 after:transition-all"
            ></span>
          </label>
        </div>
      </div>
    </div>

    <div class="w-full h-[1px] bg-textbox-border rounded-full mb-6"></div>

    <!-- Answers -->
    @for (answer of answers; track answer; let i = $index) {
      <div class="mb-5">
        <!-- Question -->
        <div class="flex items-start gap-3 mb-3">
          <div
            class="w-[30px] h-[30px] min-w-[30px] bg-textbox-fill border border-textbox-border rounded-[8px] flex items-center justify-center inter-500 text-[12px] text-base-200"
          >
            {{ i + 1 }}
          </div>
          <p class="inter-500 text-[14px] text-inverted-neutral pt-1">
            {{ answer.questionText }}
          </p>
        </div>

        <!-- Answer block -->
        <div class="ml-[42px]">
          @if (
            answer.questionType === QuestionType.FreeText ||
            answer.questionType === QuestionType.Date
          ) {
            <div
              class="bg-base-100 border border-textbox-border rounded-[12px] px-4 py-3"
            >
              <span class="inter-300 text-[14px] text-inverted-neutral">
                @if (answer.questionType === QuestionType.Date) {
                  {{ answer.freeText | date: 'dd/MM/yyyy' }}
                } @else {
                  {{ answer.freeText }}
                }
              </span>
            </div>
          }

          @if (
            answer.questionType === QuestionType.SingleChoice ||
            answer.questionType === QuestionType.MultipleChoice
          ) {
            <div class="flex flex-wrap gap-2">
              @for (
                answerOptionText of answer.answerOptionTexts;
                track answerOptionText
              ) {
                <div
                  class="bg-base-100 border border-textbox-border rounded-full px-4 py-1.5 flex items-center gap-2"
                >
                  <div
                    class="w-[6px] h-[6px] rounded-full bg-kek-purple flex-shrink-0"
                  ></div>
                  <span class="inter-300 text-[13px] text-inverted-neutral">{{
                    answerOptionText
                  }}</span>
                </div>
              }
            </div>
          }

          @if (answer.questionType === QuestionType.Ranking) {
            <div class="flex flex-col gap-2">
              @for (
                answerOptionText of answer.answerOptionTexts;
                track answerOptionText;
                let i = $index
              ) {
                <div
                  class="bg-base-100 border border-textbox-border rounded-[12px] px-4 py-2 flex items-center gap-3"
                >
                  <div
                    class="w-[22px] h-[22px] bg-textbox-fill border border-textbox-border rounded-[6px] flex items-center justify-center inter-500 text-[11px] text-kek-purple flex-shrink-0"
                  >
                    {{ i + 1 }}
                  </div>
                  <span class="inter-300 text-[13px] text-inverted-neutral">{{
                    answerOptionText
                  }}</span>
                </div>
              }
            </div>
          }

          @if (answer.questionType === QuestionType.RangeSelection) {
            <div
              class="bg-base-100 border border-textbox-border rounded-[14px] px-6 py-5"
            >
              <div class="flex w-full mb-2">
                @for (
                  answerOption of answer.answerOptionTexts;
                  track answerOption
                ) {
                  <div
                    class="flex-1 inter-300 text-[12px] text-base-200 inline-block text-center first:text-left last:text-right"
                  >
                    {{ answerOption }}
                  </div>
                }
              </div>
              <input
                class="w-full"
                type="range"
                disabled
                min="0"
                [max]="answer.answerOptionTexts.length - 1"
                [value]="answer.sliderUpperBound"
              />
            </div>
          }
        </div>
      </div>
    }

    @if (answers.length === 0) {
      <p class="inter-300 text-base-200 text-center py-8">
        No answers recorded for this response.
      </p>
    }
  </div>
}
